import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Loader2, Save, Search, SlidersHorizontal, Undo2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { confirmAction } from "@/lib/dialogs";
import { getErrorMessage } from "@/lib/errors";
import { resetPermissionsCache } from "@/lib/permissions";
import { TREE_ACTIONS, appliesTo, changedModules, childSummary, closedByParent, descendants, draftFrom, flatten, searchTree } from "@/lib/accessTree";

const LABEL = { VIEW: "View", CREATE: "Create", EDIT: "Edit", DELETE: "Delete", APPROVE: "Approve", POST: "Post", VERIFY: "Verify", PAY: "Pay" };

/** Administration → Access: the one place to say who can do what. Pick a role or a person; the same tree and the same boxes either way. */
export default function Access() {
  const [params, setParams] = useSearchParams();
  const [roles, setRoles] = useState([]);
  const [people, setPeople] = useState([]);
  const [listError, setListError] = useState(null);
  const userId = params.get("user");
  const roleId = params.get("role");
  const scope = userId ? "user" : "role";
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/rbac/matrix"), api.get("/users")])
      .then(([{ data: m }, { data: u }]) => {
        setRoles(m.roles || []); setPeople((u || []).filter((x) => x.is_active && x.role !== "ORG_ADMIN"));
        if (!params.get("user") && !params.get("role")) setParams({ role: (m.roles || []).find((r) => !r.is_system)?.id || "" }, { replace: true });
      })
      .catch((err) => setListError(getErrorMessage(err)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = async (next) => {
    if (dirty && !(await confirmAction("You have unsaved changes. Leave them behind?"))) return;
    setParams(next);
  };

  return (
    <div className="space-y-4 p-6" data-testid="access-page">
      <PageHeader title="Access" description="Choose a role to set what everyone in it can do, or a person to give them something different. Each switch is independent; anything you leave alone follows the role." />
      {listError && <p className="text-sm text-destructive" role="alert">{listError}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border p-0.5" role="tablist">
          <Button size="sm" variant={scope === "role" ? "default" : "ghost"} onClick={() => go({ role: roles.find((r) => !r.is_system)?.id || "" })} data-testid="scope-role">Role</Button>
          <Button size="sm" variant={scope === "user" ? "default" : "ghost"} onClick={() => go({ user: people[0]?.id || "" })} data-testid="scope-user">Person</Button>
        </div>
        {scope === "role" ? (
          <Select value={roleId || ""} onValueChange={(v) => go({ role: v })}>
            <SelectTrigger className="w-64" aria-label="Role"><SelectValue placeholder="Choose a role" /></SelectTrigger>
            <SelectContent>{roles.filter((r) => !r.is_system).map((r) => <SelectItem key={r.id} value={r.id}>{r.label} · {r.users} {r.users === 1 ? "person" : "people"}</SelectItem>)}</SelectContent>
          </Select>
        ) : (
          <Select value={userId || ""} onValueChange={(v) => go({ user: v })}>
            <SelectTrigger className="w-72" aria-label="Person"><SelectValue placeholder="Choose a person" /></SelectTrigger>
            <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.full_name} · {p.role}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {scope === "user" && userId && <Button asChild variant="link" size="sm"><Link to={`/erp/governance/users/${userId}`}>Their projects & “why can’t they?”</Link></Button>}
        <Button asChild variant="link" size="sm" className="ml-auto"><Link to="/erp/governance/access-history">Access history</Link></Button>
      </div>
      {(scope === "role" ? roleId : userId) ? <AccessTree key={`${scope}-${roleId || userId}`} scope={scope} id={roleId || userId} onDirty={setDirty} /> : <p className="text-sm text-muted-foreground">Choose a role or a person to begin.</p>}
    </div>
  );
}

export function AccessTree({ scope, id, onDirty = () => {} }) {
  const base = scope === "role" ? `/rbac/roles/${id}/permission-tree` : `/rbac/users/${id}/permission-tree`;
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState(new Set());
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);
  const [extras, setExtras] = useState(null);

  const load = useCallback(async () => {
    try { const { data: d } = await api.get(base); setData(d); setDraft(draftFrom(d.tree)); setFailed(null); } catch (err) { setFailed(getErrorMessage(err)); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data ? flatten(data.tree) : []), [data]);
  const byKey = useMemo(() => Object.fromEntries(rows.map(({ node }) => [node.key, node])), [rows]);
  const found = useMemo(() => (data ? searchTree(data.tree, query) : null), [data, query]);
  const changed = useMemo(() => (data ? changedModules(data.tree, draft) : []), [data, draft]);
  useEffect(() => { onDirty(changed.length > 0); }, [changed.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!changed.length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [changed.length]);

  const isOpen = (key) => open.has(key) || (found?.open.has(key) ?? false);
  const visible = rows.filter(({ node, parentKey }) => {
    if (found && !found.show.has(node.key)) return false;
    let p = parentKey;
    while (p) { if (!isOpen(p)) return false; p = byKey[p]?.parent || null; }
    return true;
  });
  const set = (key, action, value) => setDraft((d) => ({ ...d, [key]: { ...d[key], [action]: value } }));
  const setAll = (value) => setDraft((d) => Object.fromEntries(Object.keys(d).map((k) => [k, Object.fromEntries(TREE_ACTIONS.map((a) => [a, appliesTo(a, k) ? value : !!d[k]?.[a]]))])));
  const applyBelow = (node) => setDraft((d) => { const next = { ...d }; descendants(node).forEach((c) => { next[c.key] = Object.fromEntries(TREE_ACTIONS.map((a) => [a, appliesTo(a, c.key) ? !!d[node.key]?.[a] : !!d[c.key]?.[a]])); }); return next; });
  const toggle = (key) => setOpen((s) => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const withKids = rows.filter(({ node }) => node.children.length).map(({ node }) => node.key);
  const isPerson = scope === "user";
  const label = isPerson ? data?.user?.full_name : data?.role?.label;

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(changed.map((k) => [k, Object.fromEntries(TREE_ACTIONS.map((a) => [a, !!draft[k][a]]))]));
      const { data: d } = await api.put(base, { permissions: body });
      setData(d); setDraft(draftFrom(d.tree)); resetPermissionsCache();
      toast.success(isPerson ? `Saved. ${label}'s access is updated; it applies on their next action.` : `Saved. Everyone in ${label} gets this on their next action.`);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setSaving(false); }
  };

  if (failed) return <div className="text-sm text-destructive" role="alert">{failed} <Button variant="link" onClick={load}>Try again</Button></div>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading access…</div>;
  if (isPerson && data.user.is_admin) return <p className="text-sm text-muted-foreground">Admins always have full access. Change their role first to manage individual features.</p>;

  return (
    <div className="space-y-3" data-testid="access-tree">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="w-64 pl-8" placeholder="Search modules and features" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search features" /></div>
        <Button variant="outline" size="sm" onClick={() => setOpen(new Set(withKids))}><ChevronsUpDown className="mr-1 h-4 w-4" /> Expand all</Button>
        <Button variant="outline" size="sm" onClick={() => setOpen(new Set())}><ChevronsDownUp className="mr-1 h-4 w-4" /> Collapse all</Button>
        <Button variant="outline" size="sm" onClick={() => setAll(true)}>Select all</Button>
        <Button variant="outline" size="sm" onClick={() => setAll(false)}>Clear all</Button>
        <div className="ml-auto flex items-center gap-2">
          {changed.length > 0 && <Badge variant="outline" className="border-amber-500 text-amber-600" data-testid="unsaved">{changed.length} unsaved change{changed.length > 1 ? "s" : ""}</Badge>}
          <Button variant="outline" size="sm" disabled={!changed.length || saving} onClick={() => setDraft(draftFrom(data.tree))}><Undo2 className="mr-1 h-4 w-4" /> Cancel changes</Button>
          <Button size="sm" disabled={!changed.length || saving} onClick={save} data-testid="save-access">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Save className="mr-1 h-4 w-4" />} Save permissions</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left"><tr>
            <th className="px-3 py-2 font-medium">Feature</th>
            {TREE_ACTIONS.map((a) => <th key={a} className="w-20 px-2 py-2 text-center font-medium">{LABEL[a]}</th>)}
            <th className="w-44 px-3 py-2" />
          </tr></thead>
          <tbody>
            {visible.length === 0 && <tr><td colSpan={8} className="px-3 py-6 text-center text-muted-foreground">Nothing matches “{query}”.</td></tr>}
            {visible.map(({ node, depth }) => {
              const kids = node.children.length > 0;
              const closed = closedByParent(node, draft, byKey);
              const differs = isPerson && TREE_ACTIONS.some((a) => !!draft[node.key]?.[a] !== !!node.role[a]);
              return (
                <tr key={node.key} className={`border-t ${closed ? "opacity-50" : ""}`} data-testid={`row-${node.key}`}>
                  <td className="px-3 py-1.5" style={{ paddingLeft: 12 + depth * 22 }}>
                    <div className="flex items-center gap-1">
                      {kids ? <button type="button" onClick={() => toggle(node.key)} aria-label={isOpen(node.key) ? "Collapse" : "Expand"} className="rounded p-0.5 hover:bg-muted">
                        {isOpen(node.key) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}</button> : <span className="w-5" />}
                      <span className="font-medium">{node.label}</span>
                      {closed && <span className="text-xs text-muted-foreground">(closed — a feature above it is off)</span>}
                      {!isPerson && node.parent && !node.custom && <span className="text-xs text-muted-foreground">follows {byKey[node.parent]?.label}</span>}
                    </div>
                    {node.covers && <div className="ml-6 text-xs text-muted-foreground">{node.covers}</div>}
                  </td>
                  {TREE_ACTIONS.map((a) => {
                    if (!appliesTo(a, node.key)) return <td key={a} className="px-2 py-1.5 text-center text-muted-foreground/40">·</td>;
                    const sum = childSummary(node, draft, a);
                    return (
                      <td key={a} className="px-2 py-1.5 text-center">
                        <Checkbox checked={!!draft[node.key]?.[a]} onCheckedChange={(v) => set(node.key, a, v === true)} aria-label={`${node.label} ${LABEL[a]}`} data-testid={`cb-${node.key}-${a}`} />
                        {sum && <div className="text-[10px] text-muted-foreground" title="Features below this one that have it on">{sum.on}/{sum.of}</div>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {isPerson && differs && <Button variant="ghost" size="sm" className="h-7 text-xs text-amber-600" onClick={() => setDraft((d) => ({ ...d, [node.key]: { ...node.role } }))} title="Put this row back to what their role gives" data-testid={`reset-${node.key}`}>Reset to role</Button>}
                    {kids && <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => applyBelow(node)} title="Copy this row's switches onto everything below it">Apply to below</Button>}
                    {!isPerson && <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setExtras(node)} title="Approval limit, rates and export" aria-label={`${node.label} limit, rates and export`}><SlidersHorizontal className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Approve and Post only matter where a feature has an approval or posting step. Verify (check a bill before it is posted) and Pay (release a payment) appear only on Bills and Payments. A feature whose parent has View off stays closed, but its own switches are remembered. {isPerson ? "“Reset to role” puts a row back to what the role gives." : "A feature that says “follows …” has no setting of its own yet."}</p>
      {extras && <ExtrasDialog roleId={id} node={extras} onClose={() => setExtras(null)} onSaved={() => { setExtras(null); resetPermissionsCache(); load(); }} />}
    </div>
  );
}

function ExtrasDialog({ roleId, node, onClose, onSaved }) {
  const [limit, setLimit] = useState(node.approval_limit ?? "");
  const [rates, setRates] = useState(node.see_rates !== false);
  const [exp, setExp] = useState(node.can_export !== false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/rbac/roles/${roleId}/modules/${node.key}`, { approval_limit: limit === "" ? null : Number(limit), see_rates: rates, can_export: exp });
      toast.success("Saved"); onSaved();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader><DialogTitle>{node.label}: limit, rates and export</DialogTitle><DialogDescription>These apply to everyone in the role.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="limit">Approval limit (₹) <span className="text-muted-foreground">— empty means no limit</span></Label>
            <Input id="limit" type="number" min="0" value={limit} onChange={(e) => setLimit(e.target.value)} /></div>
          <div className="flex items-center justify-between"><Label htmlFor="rates">Can see rates</Label><Switch id="rates" checked={rates} onCheckedChange={setRates} /></div>
          <div className="flex items-center justify-between"><Label htmlFor="exp">Can export and print</Label><Switch id="exp" checked={exp} onCheckedChange={setExp} /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={busy}>{busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
